import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LifeBuoy, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
      <span
        aria-hidden
        className="h-8 w-8 rounded-lg bg-[#A7B584] grid place-items-center text-white font-semibold tracking-tight"
      >
        H4
      </span>
      <div className="flex flex-col leading-none">
        <span className="font-semibold text-[#5A5B5C] tracking-tight">Help4Emergency</span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A7B584]">KI-Leitstelle</span>
      </div>
    </Link>
  );
}

function AlphaPill() {
  return (
    <span
      data-testid="alpha-badge-nav"
      className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-[#FCEBD6] text-[#B36B15] tracking-wide"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[#B36B15] animate-pulse" />
      Alpha · Early Tester
    </span>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const navLinkClass = ({ isActive }) =>
    `text-sm text-[#272525] hover:text-[#5A5B5C] transition-colors ${
      isActive ? "text-[#5A5B5C] font-medium" : ""
    }`;

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#FCFCFB]/85 border-b border-[#EAEBE6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <Brand />
          <AlphaPill />
        </div>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primär">
          <NavLink to="/#loesung" className={navLinkClass} data-testid="nav-loesung">Lösung</NavLink>
          <NavLink to="/#features" className={navLinkClass} data-testid="nav-features">Features</NavLink>
          <NavLink to="/#preise" className={navLinkClass} data-testid="nav-preise">Preise</NavLink>
          <NavLink to="/support" className={navLinkClass} data-testid="nav-support">Support</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="text-sm text-[#272525] hover:text-[#5A5B5C] px-3 py-2"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-sm"
                data-testid="nav-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1.5" /> Abmelden
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-[#272525] hover:text-[#5A5B5C] px-3 py-2"
                data-testid="nav-login"
              >
                Anmelden
              </Link>
              <Link to="/register" data-testid="nav-register-btn">
                <Button className="bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg px-4">
                  Early-Tester werden
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-lg text-[#5A5B5C] hover:bg-[#F4F5F2]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#EAEBE6] bg-[#FCFCFB]" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            <a href="/#loesung" onClick={() => setOpen(false)} className="text-sm text-[#272525] py-2">Lösung</a>
            <a href="/#features" onClick={() => setOpen(false)} className="text-sm text-[#272525] py-2">Features</a>
            <a href="/#preise" onClick={() => setOpen(false)} className="text-sm text-[#272525] py-2">Preise</a>
            <Link to="/support" onClick={() => setOpen(false)} className="text-sm text-[#272525] py-2 inline-flex items-center gap-2">
              <LifeBuoy className="h-4 w-4" /> Support
            </Link>
            <div className="h-px bg-[#EAEBE6] my-2" />
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm py-2">Dashboard</Link>
                <button onClick={handleLogout} className="text-sm py-2 text-left">Abmelden</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm py-2">Anmelden</Link>
                <Link to="/register" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-white">
                    Early-Tester werden
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
